import type { StructuredRisk } from '../../utils/dashboard';
import { dashboardCopy } from '../../copy/dashboard';
import WhyAffordance from '../ai/WhyAffordance';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// RiskStrip — surfaces only at-risk and breach items. Absence is the good
// state; when there are no risks we show a single calm "on track" line.
// ---------------------------------------------------------------------------

interface RiskStripProps {
  risks: StructuredRisk[];
  loading?: boolean;
  /** Optional narrative from the AI describing why the flagged risks matter.
   *  Shown behind a "Why?" affordance next to the header so the strip stays
   *  compact. */
  aiRationale?: string | null;
}

export function RiskStrip({ risks, loading = false, aiRationale }: RiskStripProps) {
  if (loading) {
    return <div className="h-12 animate-pulse rounded-2xl bg-card/60" />;
  }

  if (risks.length === 0) {
    return (
      <div className="flex items-center gap-3 rounded-2xl border border-border bg-card/60 px-5 py-3 backdrop-blur">
        <span aria-hidden className="h-2 w-2 rounded-full bg-emerald-500" />
        <p className="text-sm text-muted-foreground">{dashboardCopy.risksEmpty}</p>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-2 flex items-center gap-1">
        <p className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
          {dashboardCopy.risksHeader}
        </p>
        <WhyAffordance rationale={aiRationale} size="compact" label="Why these risks" />
      </div>
      <div className="flex flex-col gap-2">
        {risks.map((risk, idx) => (
          <RiskRow key={`${risk.type}-${idx}`} risk={risk} />
        ))}
      </div>
    </div>
  );
}

// Severity drives a thin left edge — colour reserved for meaning, not
// decoration. Each variant carries explicit bar / badge classes so reordering
// the class string (or adding utility classes) can't silently shift indices.
const RISK_VARIANTS = {
  breach: {
    bar: 'bg-rose-500',
    badge: 'text-rose-600 dark:text-rose-400 border-rose-500/60',
    label: 'Breach',
  },
  atRisk: {
    bar: 'bg-amber-500',
    badge: 'text-amber-700 dark:text-amber-400 border-amber-500/60',
    label: 'At risk',
  },
} as const;

function RiskRow({ risk }: { risk: StructuredRisk }) {
  const variant = risk.severity === 'breach' ? RISK_VARIANTS.breach : RISK_VARIANTS.atRisk;

  return (
    <div className="relative flex items-start gap-3 rounded-2xl border border-border bg-card/60 px-5 py-3 pl-6 backdrop-blur">
      <span
        aria-hidden
        className={cn('absolute left-0 top-2 bottom-2 w-[3px] rounded', variant.bar)}
      />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="text-sm font-semibold">{risk.label}</p>
          <span
            className={cn(
              'inline-flex items-center rounded-full border bg-transparent px-1.5 py-0.5 text-[10px] font-bold uppercase leading-none tracking-[0.08em]',
              variant.badge,
            )}
          >
            {variant.label}
          </span>
        </div>
        {risk.detail && (
          <p className="mt-0.5 text-xs text-muted-foreground">{risk.detail}</p>
        )}
      </div>
    </div>
  );
}

export default RiskStrip;
